/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { ReflectionPhone } from './reflection-phone'
import { ReflectionPhoneRes } from './reflection-phone-res'
import { InformalReflection } from './informal-reflection'
import { BatchUpdateWithIdReq } from './batch-update-with-id-req'
import { InformalReflectionReq } from './informal-reflection-req'
import { QueryInformalStudentsQuery } from './query-informal-students-query'
import { BatchDisableInformalStudentsQuery } from './batch-disable-informal-students-query'
import { QueryInformalStudentsListQuery } from './query-informal-students-list-query'
import { LoadInformalStudentQuery } from './load-informal-student-query'
import { QueryInformalTeachersQuery } from './query-informal-teachers-query'
import { BatchDisableInformalTeachersQuery } from './batch-disable-informal-teachers-query'
import { QueryInformalTeachersListQuery } from './query-informal-teachers-list-query'
import { LoadInformalTeacherQuery } from './load-informal-teacher-query'

const buildConfig = createConfigBuilder('growp')

export const informalReflectionApi = {
  loadInformalSamePhoneConfig(
    reflectionPhone: ReflectionPhone,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/informal_reflection/same-phone/guardians',
      {},
      null,
      reflectionPhone,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 非正式用户-学生-相同手机号查询
   * @param reflectionPhone
   * @param [options]
   * @return AxiosResponsePromise<ReflectionPhoneRes>
   */
  loadInformalSamePhone(
    reflectionPhone: ReflectionPhone,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<ReflectionPhoneRes> {
    return axios(
      informalReflectionApi.loadInformalSamePhoneConfig(
        reflectionPhone,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new ReflectionPhoneRes(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadInformalSamePhone(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: ReflectionPhone) =>
        informalReflectionApi.loadInformalSamePhone(payload),
      mutationOptions
    )
  },

  queryInformalStudentsConfig(
    queryInformalStudentsQuery?: QueryInformalStudentsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/informal_reflection/students',
      {},
      queryInformalStudentsQuery
    )
  },

  /**
   * @summary 非正式用户-学生-列表查询
   * @param [queryInformalStudentsQuery]
   * @param [queryInformalStudentsQuery.status] 状态查询
   * @param [queryInformalStudentsQuery.expand]
   * @param [queryInformalStudentsQuery.notificationId] 获取 expand:is_read_notification 时必须传
   * @param [queryInformalStudentsQuery.isReadNotification] 是否读了某通知
   * @param [queryInformalStudentsQuery.paginated] 是否分页，默认 1
   * @param [queryInformalStudentsQuery.page] 显示的数据页，默认 1
   * @param [queryInformalStudentsQuery.perPage] 每页显示数量，默认 20
   * @param [queryInformalStudentsQuery.sort]
   * @param [queryInformalStudentsQuery.nameIn]
   * @param [queryInformalStudentsQuery.nameLike] 名字模糊查询
   * @param [queryInformalStudentsQuery.accountIn] 多个账户查询
   * @param [queryInformalStudentsQuery.accountLike] 账户模糊查询
   * @param [queryInformalStudentsQuery.phone]
   * @param [queryInformalStudentsQuery.phoneLike] 手机号模糊查询
   * @param [queryInformalStudentsQuery.phoneIn]
   * @param [queryInformalStudentsQuery.usinIn] 学工号查询
   * @param [queryInformalStudentsQuery.usinLike] 学工号模糊查询
   * @param [queryInformalStudentsQuery.createdAtEgt] 创建时间大于
   * @param [queryInformalStudentsQuery.createdAtElt] 创建时间小于
   * @param [queryInformalStudentsQuery.statusIn]
   * @param [queryInformalStudentsQuery.roleIn]
   * @param [queryInformalStudentsQuery.idIn]
   * @param [options]
   * @return AxiosResponsePromise<InformalReflection[]>
   */
  queryInformalStudents<TQuery extends QueryInformalStudentsQuery>(
    queryInformalStudentsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<InformalReflection, TQuery['expand']>[]> {
    return axios(
      informalReflectionApi.queryInformalStudentsConfig(
        queryInformalStudentsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data
        ? _data.map((d: any) => new InformalReflection(d))
        : _data
      return { data, ...rest }
    }) as any
  },

  useQueryInformalStudents<TQuery extends QueryInformalStudentsQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<InformalReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<InformalReflection, TQuery['expand']>[]>(
      informalReflectionApi.queryInformalStudents,
      'InformalReflection.queryInformalStudents',
      queryOptions,
      query
    )
  },

  batchDeleteInformalStudentsConfig(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'delete',
      '/informal_reflection/students/$batch',
      {},
      null,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 非正式用户-学生-批量删除
   * @param batchUpdateWithIdReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchDeleteInformalStudents(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      informalReflectionApi.batchDeleteInformalStudentsConfig(
        batchUpdateWithIdReq,
        options
      )
    ) as any
  },

  useBatchDeleteInformalStudents(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: BatchUpdateWithIdReq) =>
        informalReflectionApi.batchDeleteInformalStudents(payload),
      mutationOptions
    )
  },

  batchDisableInformalStudentsConfig(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    batchDisableInformalStudentsQuery?: BatchDisableInformalStudentsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/informal_reflection/students/disable/$batch',
      {},
      batchDisableInformalStudentsQuery,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 非正式用户-学生-批量禁用
   * @param batchUpdateWithIdReq
   * @param [batchDisableInformalStudentsQuery]
   * @param [batchDisableInformalStudentsQuery.disabled] 是否禁用-默认是
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchDisableInformalStudents(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    batchDisableInformalStudentsQuery?: BatchDisableInformalStudentsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      informalReflectionApi.batchDisableInformalStudentsConfig(
        batchUpdateWithIdReq,
        batchDisableInformalStudentsQuery,
        options
      )
    ) as any
  },

  useBatchDisableInformalStudents(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        payload,
        query,
      }: {
        payload: BatchUpdateWithIdReq
        query?: BatchDisableInformalStudentsQuery
      }) => informalReflectionApi.batchDisableInformalStudents(payload, query),
      mutationOptions
    )
  },

  batchEnrollInformalStudentsConfig(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/informal_reflection/students/enroll/$batch',
      {},
      null,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 非正式用户-学生-批量录取
   * @param batchUpdateWithIdReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchEnrollInformalStudents(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      informalReflectionApi.batchEnrollInformalStudentsConfig(
        batchUpdateWithIdReq,
        options
      )
    ) as any
  },

  useBatchEnrollInformalStudents(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: BatchUpdateWithIdReq) =>
        informalReflectionApi.batchEnrollInformalStudents(payload),
      mutationOptions
    )
  },

  queryExportedInformalStudentsConfig(
    keys: string,
    idIn: string,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/informal_reflection/students/exported',
      {
        keys,
        idIn,
      }
    )
  },

  /**
   * @summary 非正式用户-学生-列表导出
   * @param keys
   * @param idIn
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  queryExportedInformalStudents(
    keys: string,
    idIn: string,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      informalReflectionApi.queryExportedInformalStudentsConfig(
        keys,
        idIn,
        options
      )
    ) as any
  },

  useQueryExportedInformalStudents(
    {
      keys,
      idIn,
    }: {
      keys: string
      idIn: string
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      informalReflectionApi.queryExportedInformalStudents,
      'InformalReflection.queryExportedInformalStudents',
      queryOptions,
      keys,
      idIn
    )
  },

  queryInformalStudentsListConfig(
    queryInformalStudentsListQuery?: QueryInformalStudentsListQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/informal_reflection/students/list',
      {},
      queryInformalStudentsListQuery
    )
  },

  /**
   * @summary 非正式用户-学生-列表查询
   * @param [queryInformalStudentsListQuery]
   * @param [queryInformalStudentsListQuery.status] 状态查询
   * @param [queryInformalStudentsListQuery.expand]
   * @param [queryInformalStudentsListQuery.paginated] 是否分页，默认 1
   * @param [queryInformalStudentsListQuery.page] 显示的数据页，默认 1
   * @param [queryInformalStudentsListQuery.perPage] 每页显示数量，默认 20
   * @param [queryInformalStudentsListQuery.sort]
   * @param [queryInformalStudentsListQuery.nameIn]
   * @param [queryInformalStudentsListQuery.nameLike] 名字模糊查询
   * @param [queryInformalStudentsListQuery.accountIn] 多个账户查询
   * @param [queryInformalStudentsListQuery.accountLike] 账户模糊查询
   * @param [queryInformalStudentsListQuery.phone]
   * @param [queryInformalStudentsListQuery.phoneLike] 手机号模糊查询
   * @param [queryInformalStudentsListQuery.phoneIn]
   * @param [queryInformalStudentsListQuery.usinIn] 学工号查询
   * @param [queryInformalStudentsListQuery.usinLike] 学工号模糊查询
   * @param [queryInformalStudentsListQuery.createdAtEgt] 创建时间大于
   * @param [queryInformalStudentsListQuery.createdAtElt] 创建时间小于
   * @param [queryInformalStudentsListQuery.statusIn]
   * @param [queryInformalStudentsListQuery.roleIn]
   * @param [queryInformalStudentsListQuery.idIn]
   * @param [options]
   * @return AxiosResponsePromise<InformalReflection[]>
   */
  queryInformalStudentsList<TQuery extends QueryInformalStudentsListQuery>(
    queryInformalStudentsListQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<InformalReflection, TQuery['expand']>[]> {
    return axios(
      informalReflectionApi.queryInformalStudentsListConfig(
        queryInformalStudentsListQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data
        ? _data.map((d: any) => new InformalReflection(d))
        : _data
      return { data, ...rest }
    }) as any
  },

  useQueryInformalStudentsList<TQuery extends QueryInformalStudentsListQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<InformalReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<InformalReflection, TQuery['expand']>[]>(
      informalReflectionApi.queryInformalStudentsList,
      'InformalReflection.queryInformalStudentsList',
      queryOptions,
      query
    )
  },

  loadInformalStudentConfig(
    id: number,
    loadInformalStudentQuery?: LoadInformalStudentQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/informal_reflection/students/{id}',
      {
        id,
      },
      loadInformalStudentQuery
    )
  },

  /**
   * @summary 非正式用户-学生-单个查询
   * @param id
   * @param [loadInformalStudentQuery]
   * @param [loadInformalStudentQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<InformalReflection>
   */
  loadInformalStudent<TQuery extends LoadInformalStudentQuery>(
    id: number,
    loadInformalStudentQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<InformalReflection, TQuery['expand']>> {
    return axios(
      informalReflectionApi.loadInformalStudentConfig(
        id,
        loadInformalStudentQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new InformalReflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadInformalStudent<TQuery extends LoadInformalStudentQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<InformalReflection, TQuery['expand']>> {
    return useQueryApi<Expand<InformalReflection, TQuery['expand']>>(
      informalReflectionApi.loadInformalStudent,
      'InformalReflection.loadInformalStudent',
      queryOptions,
      id,
      query
    )
  },

  updateInformalStudentConfig(
    id: number,
    informalReflectionReq: InformalReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/informal_reflection/students/{id}',
      {
        id,
      },
      null,
      informalReflectionReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          name: (v: any) => v || '',
          usin: (v: any) => v || '',
          account: (v: any) => v || '',
          role: (v: any) => v || null,
          phone: (v: any) => v || '',
          email: (v: any) => v || '',
          avatar: (v: any) => v || '',
          photo: (v: any) => v || '',
          gender: (v: any) => v || null,
          status: (v: any) => v || null,
          idcard: (v: any) => v || '',
          idcardType: (v: any) => v || null,
          graduate: (v: any) => v || '',
          registedAt: (v: any) => v || '',
          birthday: (v: any) => v || '',
          guardians: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 非正式用户-学生-单个编辑
   * @param id
   * @param informalReflectionReq
   * @param [options]
   * @return AxiosResponsePromise<InformalReflection>
   */
  updateInformalStudent(
    id: number,
    informalReflectionReq: InformalReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<InformalReflection> {
    return axios(
      informalReflectionApi.updateInformalStudentConfig(
        id,
        informalReflectionReq,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new InformalReflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateInformalStudent(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: InformalReflectionReq }) =>
        informalReflectionApi.updateInformalStudent(id, payload),
      mutationOptions
    )
  },

  queryInformalTeachersConfig(
    queryInformalTeachersQuery?: QueryInformalTeachersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/informal_reflection/teachers',
      {},
      queryInformalTeachersQuery
    )
  },

  /**
   * @summary 非正式用户-教师-列表查询
   * @param [queryInformalTeachersQuery]
   * @param [queryInformalTeachersQuery.status] 状态查询
   * @param [queryInformalTeachersQuery.expand]
   * @param [queryInformalTeachersQuery.notificationId] 获取 expand:is_read_notification 时必须传
   * @param [queryInformalTeachersQuery.isReadNotification] 是否读了某通知
   * @param [queryInformalTeachersQuery.paginated] 是否分页，默认 1
   * @param [queryInformalTeachersQuery.page] 显示的数据页，默认 1
   * @param [queryInformalTeachersQuery.perPage] 每页显示数量，默认 20
   * @param [queryInformalTeachersQuery.sort]
   * @param [queryInformalTeachersQuery.nameIn]
   * @param [queryInformalTeachersQuery.nameLike] 名字模糊查询
   * @param [queryInformalTeachersQuery.accountIn] 多个账户查询
   * @param [queryInformalTeachersQuery.accountLike] 账户模糊查询
   * @param [queryInformalTeachersQuery.phone]
   * @param [queryInformalTeachersQuery.phoneLike] 手机号模糊查询
   * @param [queryInformalTeachersQuery.phoneIn]
   * @param [queryInformalTeachersQuery.usinIn] 学工号查询
   * @param [queryInformalTeachersQuery.usinLike] 学工号模糊查询
   * @param [queryInformalTeachersQuery.createdAtEgt] 创建时间大于
   * @param [queryInformalTeachersQuery.createdAtElt] 创建时间小于
   * @param [queryInformalTeachersQuery.statusIn]
   * @param [queryInformalTeachersQuery.roleIn]
   * @param [queryInformalTeachersQuery.idIn]
   * @param [options]
   * @return AxiosResponsePromise<InformalReflection[]>
   */
  queryInformalTeachers<TQuery extends QueryInformalTeachersQuery>(
    queryInformalTeachersQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<InformalReflection, TQuery['expand']>[]> {
    return axios(
      informalReflectionApi.queryInformalTeachersConfig(
        queryInformalTeachersQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data
        ? _data.map((d: any) => new InformalReflection(d))
        : _data
      return { data, ...rest }
    }) as any
  },

  useQueryInformalTeachers<TQuery extends QueryInformalTeachersQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<InformalReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<InformalReflection, TQuery['expand']>[]>(
      informalReflectionApi.queryInformalTeachers,
      'InformalReflection.queryInformalTeachers',
      queryOptions,
      query
    )
  },

  batchDeleteInformalTeachersConfig(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'delete',
      '/informal_reflection/teachers/$batch',
      {},
      null,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 非正式用户-教师-批量删除
   * @param batchUpdateWithIdReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchDeleteInformalTeachers(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      informalReflectionApi.batchDeleteInformalTeachersConfig(
        batchUpdateWithIdReq,
        options
      )
    ) as any
  },

  useBatchDeleteInformalTeachers(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: BatchUpdateWithIdReq) =>
        informalReflectionApi.batchDeleteInformalTeachers(payload),
      mutationOptions
    )
  },

  batchDisableInformalTeachersConfig(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    batchDisableInformalTeachersQuery?: BatchDisableInformalTeachersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/informal_reflection/teachers/disable/$batch',
      {},
      batchDisableInformalTeachersQuery,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 非正式用户-教师-批量禁用
   * @param batchUpdateWithIdReq
   * @param [batchDisableInformalTeachersQuery]
   * @param [batchDisableInformalTeachersQuery.disabled] 是否禁用-默认是
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchDisableInformalTeachers(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    batchDisableInformalTeachersQuery?: BatchDisableInformalTeachersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      informalReflectionApi.batchDisableInformalTeachersConfig(
        batchUpdateWithIdReq,
        batchDisableInformalTeachersQuery,
        options
      )
    ) as any
  },

  useBatchDisableInformalTeachers(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        payload,
        query,
      }: {
        payload: BatchUpdateWithIdReq
        query?: BatchDisableInformalTeachersQuery
      }) => informalReflectionApi.batchDisableInformalTeachers(payload, query),
      mutationOptions
    )
  },

  batchEnrollInformalTeachersConfig(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/informal_reflection/teachers/enroll/$batch',
      {},
      null,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 非正式用户-教师-批量录取
   * @param batchUpdateWithIdReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchEnrollInformalTeachers(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      informalReflectionApi.batchEnrollInformalTeachersConfig(
        batchUpdateWithIdReq,
        options
      )
    ) as any
  },

  useBatchEnrollInformalTeachers(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: BatchUpdateWithIdReq) =>
        informalReflectionApi.batchEnrollInformalTeachers(payload),
      mutationOptions
    )
  },

  queryExportedInformalTeachersConfig(
    keys: string,
    idIn: string,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/informal_reflection/teachers/exported',
      {
        keys,
        idIn,
      }
    )
  },

  /**
   * @summary 非正式用户-教师-列表导出
   * @param keys
   * @param idIn
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  queryExportedInformalTeachers(
    keys: string,
    idIn: string,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      informalReflectionApi.queryExportedInformalTeachersConfig(
        keys,
        idIn,
        options
      )
    ) as any
  },

  useQueryExportedInformalTeachers(
    {
      keys,
      idIn,
    }: {
      keys: string
      idIn: string
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      informalReflectionApi.queryExportedInformalTeachers,
      'InformalReflection.queryExportedInformalTeachers',
      queryOptions,
      keys,
      idIn
    )
  },

  queryInformalTeachersListConfig(
    queryInformalTeachersListQuery?: QueryInformalTeachersListQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/informal_reflection/teachers/list',
      {},
      queryInformalTeachersListQuery
    )
  },

  /**
   * @summary 非正式用户-老师-列表查询
   * @param [queryInformalTeachersListQuery]
   * @param [queryInformalTeachersListQuery.status] 状态查询
   * @param [queryInformalTeachersListQuery.expand]
   * @param [queryInformalTeachersListQuery.paginated] 是否分页，默认 1
   * @param [queryInformalTeachersListQuery.page] 显示的数据页，默认 1
   * @param [queryInformalTeachersListQuery.perPage] 每页显示数量，默认 20
   * @param [queryInformalTeachersListQuery.sort]
   * @param [queryInformalTeachersListQuery.nameIn]
   * @param [queryInformalTeachersListQuery.nameLike] 名字模糊查询
   * @param [queryInformalTeachersListQuery.accountIn] 多个账户查询
   * @param [queryInformalTeachersListQuery.accountLike] 账户模糊查询
   * @param [queryInformalTeachersListQuery.phone]
   * @param [queryInformalTeachersListQuery.phoneLike] 手机号模糊查询
   * @param [queryInformalTeachersListQuery.phoneIn]
   * @param [queryInformalTeachersListQuery.usinIn] 学工号查询
   * @param [queryInformalTeachersListQuery.usinLike] 学工号模糊查询
   * @param [queryInformalTeachersListQuery.createdAtEgt] 创建时间大于
   * @param [queryInformalTeachersListQuery.createdAtElt] 创建时间小于
   * @param [queryInformalTeachersListQuery.statusIn]
   * @param [queryInformalTeachersListQuery.roleIn]
   * @param [queryInformalTeachersListQuery.idIn]
   * @param [options]
   * @return AxiosResponsePromise<InformalReflection[]>
   */
  queryInformalTeachersList<TQuery extends QueryInformalTeachersListQuery>(
    queryInformalTeachersListQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<InformalReflection, TQuery['expand']>[]> {
    return axios(
      informalReflectionApi.queryInformalTeachersListConfig(
        queryInformalTeachersListQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data
        ? _data.map((d: any) => new InformalReflection(d))
        : _data
      return { data, ...rest }
    }) as any
  },

  useQueryInformalTeachersList<TQuery extends QueryInformalTeachersListQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<InformalReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<InformalReflection, TQuery['expand']>[]>(
      informalReflectionApi.queryInformalTeachersList,
      'InformalReflection.queryInformalTeachersList',
      queryOptions,
      query
    )
  },

  loadInformalTeacherConfig(
    id: number,
    loadInformalTeacherQuery?: LoadInformalTeacherQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/informal_reflection/teachers/{id}',
      {
        id,
      },
      loadInformalTeacherQuery
    )
  },

  /**
   * @summary 非正式用户-教师-单个查询
   * @param id
   * @param [loadInformalTeacherQuery]
   * @param [loadInformalTeacherQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<InformalReflection>
   */
  loadInformalTeacher<TQuery extends LoadInformalTeacherQuery>(
    id: number,
    loadInformalTeacherQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<InformalReflection, TQuery['expand']>> {
    return axios(
      informalReflectionApi.loadInformalTeacherConfig(
        id,
        loadInformalTeacherQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new InformalReflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadInformalTeacher<TQuery extends LoadInformalTeacherQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<InformalReflection, TQuery['expand']>> {
    return useQueryApi<Expand<InformalReflection, TQuery['expand']>>(
      informalReflectionApi.loadInformalTeacher,
      'InformalReflection.loadInformalTeacher',
      queryOptions,
      id,
      query
    )
  },

  updateInformalTeacherConfig(
    id: number,
    informalReflectionReq: InformalReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/informal_reflection/teachers/{id}',
      {
        id,
      },
      null,
      informalReflectionReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          name: (v: any) => v || '',
          usin: (v: any) => v || '',
          account: (v: any) => v || '',
          role: (v: any) => v || null,
          phone: (v: any) => v || '',
          email: (v: any) => v || '',
          avatar: (v: any) => v || '',
          photo: (v: any) => v || '',
          gender: (v: any) => v || null,
          status: (v: any) => v || null,
          idcard: (v: any) => v || '',
          idcardType: (v: any) => v || null,
          graduate: (v: any) => v || '',
          registedAt: (v: any) => v || '',
          birthday: (v: any) => v || '',
          guardians: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 非正式用户-教师-单个编辑
   * @param id
   * @param informalReflectionReq
   * @param [options]
   * @return AxiosResponsePromise<InformalReflection>
   */
  updateInformalTeacher(
    id: number,
    informalReflectionReq: InformalReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<InformalReflection> {
    return axios(
      informalReflectionApi.updateInformalTeacherConfig(
        id,
        informalReflectionReq,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new InformalReflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateInformalTeacher(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: InformalReflectionReq }) =>
        informalReflectionApi.updateInformalTeacher(id, payload),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(informalReflectionApi).forEach((api) => {
  // @ts-ignore
  informalReflectionApi[api]._name_ = `InformalReflection.${api}`
})
